import withDefaultProps from '@anm/HOCs/withDefaultProps';
import noop from '@anm/helpers/noop';
import { Component, ComponentType } from 'react';

import changeDateDay from '../helpers/date/changeDateDay';
import correctDateFormat from '../helpers/date/correctDateFormat';
import getDatesRange from '../helpers/date/getDatesRange';
import getDayTitle from '../helpers/date/getDayTitle';
import getMonthTitle from '../helpers/date/getMonthTitle';
import isCurrentDay from '../helpers/date/isCurrentDay';

type CalendarDay = {
  date: number;
  title: string;
  isActive: boolean;
  year: any;
  month: any;
};

export type WithCalendarState = {
  endDate: Date;
  startDate: Date;
};

export type WithCalendarProps = {
  startWeekDate: string;
  endWeekDate: string;
} & Partial<DefaultProps>;

export type CalendarInfo = {
  days: CalendarDay[];
  year: number;
  month: number;
  monthTitle: string;
};

export type WithCalendarInsertedProps = WithCalendarProps &
  WithCalendarState &
  ExternalProps;

type ExternalProps = {
  calendarInfo: CalendarInfo;
  toDay: () => void;
  toNextWeek: () => void;
  toPreviousWeek: () => void;
};

type DefaultProps = {
  onStartDateChange: (date: Date) => void;
};

const defaultProps = {
  onStartDateChange: noop
};

const withCalendar = <P extends {}>(
  WrappedComponent: ComponentType<P & WithCalendarInsertedProps>
) => {
  class WithCalendar extends Component<
    P & WithCalendarProps & DefaultProps,
    WithCalendarState
  > {
    static displayName = `withCalendar(${WrappedComponent.name ||
      'BaseComponent'})`;

    static getDerivedStateFromProps({
      startWeekDate,
      endWeekDate
    }: WithCalendarProps) {
      return {
        endDate: new Date(correctDateFormat(endWeekDate)),
        startDate: new Date(correctDateFormat(startWeekDate))
      };
    }

    state = {
      endDate: new Date(),
      startDate: new Date()
    };

    toDay = () => {
      const { onStartDateChange } = this.props;
      onStartDateChange(new Date());
    };

    toPreviousWeek = () => {
      const { startDate } = this.state;
      const { onStartDateChange } = this.props;
      const newEndDate = changeDateDay(startDate!, -1);
      const newStartDate = changeDateDay(newEndDate!, -6);

      onStartDateChange(newStartDate);
    };

    toNextWeek = () => {
      const { endDate } = this.state;
      const { onStartDateChange } = this.props;
      const newStartDate = changeDateDay(endDate!, 1);

      onStartDateChange(newStartDate);
    };

    getCalendarInfo() {
      const { endDate, startDate } = this.state;
      const year = startDate.getFullYear();
      const month = startDate.getMonth();
      const monthTitle = getMonthTitle(startDate, endDate);
      const datesRange = getDatesRange(startDate, endDate);

      const days = datesRange.map<CalendarDay>(dateItem => {
        return {
          year: dateItem.getFullYear(),
          month: dateItem.getMonth(),
          date: dateItem.getDate(),
          title: getDayTitle(dateItem),
          isActive: isCurrentDay(dateItem)
        };
      });

      return { days, year, month, monthTitle };
    }

    render() {
      return (
        <WrappedComponent
          {...this.state}
          calendarInfo={this.getCalendarInfo()}
          toDay={this.toDay}
          toNextWeek={this.toNextWeek}
          toPreviousWeek={this.toPreviousWeek}
          {...this.props}
        />
      );
    }
  }

  type PropsExcludingCalendarState = Pick<
    P,
    Exclude<keyof P, keyof (WithCalendarState & ExternalProps)>
  >;
  type RecomposeProps = Partial<WithCalendarState> &
    Partial<ExternalProps> &
    PropsExcludingCalendarState &
    WithCalendarProps;

  return withDefaultProps(defaultProps)(
    (WithCalendar as ComponentType<any>) as ComponentType<RecomposeProps>
  );
};

export default withCalendar;
